/**
 * Pick borrower names from the names the documents actually carry (DOP-454).
 *
 * Strict on purpose. The package resolver matches a borrower's files by the
 * filename prefix, so a name typed a shade differently from the deal room
 * ("Apex Mfg." for "Apex Manufacturing") creates a borrower whose package never
 * arrives, and the failure surfaces two screens later as an empty board cell
 * that blames the upload. So what is typed here FILTERS; it never becomes a
 * value. `values` only ever holds names that were chosen from the list.
 *
 * MULTI-select since DOP-468: a firm opening a deal room adds several borrowers
 * at once, and doing that one name at a time through a form that resets after
 * every add is the same work six times over. Each option carries a checkbox,
 * picking toggles it and leaves the list open, and the chosen names sit in the
 * box as removable chips. The query is now purely a filter — it never holds a
 * chosen name — which is what lets the two share one box.
 *
 * It also answers ONE question when asked to (DOP-469): `mode="single"` picks
 * one name, closes the list behind it and shows it in the box, with a radio dot
 * in the same leading slot the checkbox occupies. That mode exists because the
 * form above used to pair this control with a native <select> for the project,
 * and two dropdowns that behave and look nothing alike, stacked side by side,
 * read as two different kinds of question when they are the same kind twice.
 *
 * No combobox library — the app has none, and adding a dependency for one field
 * is a worse trade than the sixty lines of keyboard handling below. The ARIA is
 * the combobox pattern: `role="combobox"` on the input, a multi-selectable
 * listbox, `aria-activedescendant` naming the highlighted option, and the
 * options carrying their real ids so a screen reader follows the arrow keys.
 *
 * Grouped by folder because a firm with two deal rooms has the same borrower in
 * both, and "which of these two Apexes" is a question only the folder answers.
 * With a single group there is no such question, so the folder heading is
 * dropped — DOP-459 chooses the project in the field above this one, and
 * repeating that one folder's name over its own options is noise.
 */

import { Check, ChevronDown, X } from "lucide-react";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

/**
 * What this picker can be pointed at. Structurally the candidate groups the API
 * serves, widened so the same control can offer something else with the same
 * shape — DOP-469 points it at the PROJECTS themselves.
 */
export interface PickerGroup {
	folder_id: number | null;
	folder_name: string;
	candidates: { name: string; file_count: number }[];
}

export interface BorrowerPickerProps {
	groups: PickerGroup[];
	/**
	 * The chosen names, empty while nothing is chosen. Never free text.
	 *
	 * One array for both modes, on purpose: `mode="single"` simply never holds
	 * more than one, so a caller switching modes — or reading a field that could
	 * be either — has one shape to handle instead of a union.
	 */
	values: string[];
	onChange: (names: string[]) => void;
	/**
	 * How many answers the list takes. `multiple` (the default) toggles each
	 * option and keeps the list open; `single` picks one, closes, and shows the
	 * chosen name in the box.
	 */
	mode?: "single" | "multiple";
	disabled?: boolean;
	/** Ties the field to its own <label>. */
	inputId?: string;
	/**
	 * What the empty box says. The default describes the search; a caller that
	 * treats "nothing picked" as a meaningful choice — DOP-459's "every borrower
	 * in this project" — says so here instead. Once something is picked the
	 * chips say what the box holds, so the placeholder steps aside.
	 */
	placeholder?: string;
	/**
	 * The muted number on the right of each option. It is what tells two similar
	 * names apart, so it is the caller's to word: files for a borrower, borrowers
	 * for a project.
	 */
	countLabel?: (count: number) => string;
	/** What the list says when the query matches nothing. */
	emptyLabel?: string;
	/**
	 * The listbox's accessible name. Two of these fields on one form need two
	 * names, or a screen reader — and a test — cannot say which list it is in.
	 */
	listLabel?: string;
}

/** One option, flattened out of the groups so the arrow keys have an order. */
interface FlatOption {
	name: string;
}

const BOX =
	"flex w-full flex-wrap items-center gap-1 rounded border border-gray-300 px-2 py-1 pr-7 text-base text-text-base focus-within:border-[#008782]";

const BorrowerPicker: React.FC<BorrowerPickerProps> = ({
	groups,
	values,
	onChange,
	mode = "multiple",
	disabled = false,
	inputId,
	placeholder = "Search your documents…",
	countLabel = (count) => `${count} ${count === 1 ? "file" : "files"}`,
	emptyLabel = "No name in your documents matches.",
	listLabel = "Borrower names",
}) => {
	const multiple = mode === "multiple";
	// In `multiple` this is what is in the box and nothing else: a chosen name
	// lives in a chip, so the query is only ever a filter. In `single` there are
	// no chips, so the box doubles as the display of the chosen name and the
	// query has to be kept in step with the parent's value.
	const [query, setQuery] = useState(() => (multiple ? "" : (values[0] ?? "")));
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const generatedId = useId();
	const listboxId = `${generatedId}-listbox`;
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const chosen = useMemo(() => new Set(values), [values]);
	const single = multiple ? null : (values[0] ?? null);

	// `single` only. The last name this picker chose: without it, a parent
	// clearing the value after a successful add left the chosen name sitting in
	// the box, with the submit disabled because nothing was picked.
	const lastPicked = useRef<string | null>(single);

	// Follow the parent in both directions: it sets a value, and it clears the
	// value after a successful submit.
	useEffect(() => {
		if (multiple) return;
		if (single !== null) {
			lastPicked.current = single;
			setQuery(single);
			return;
		}
		// Only the pick is cleared, never what someone is midway through typing.
		// Typing sets `query` first and clears the value second, so a query that
		// still equals the last pick is the one case where nobody has typed
		// anything since that pick.
		setQuery((current) => {
			if (current !== lastPicked.current) return current;
			lastPicked.current = null;
			return "";
		});
	}, [multiple, single]);

	const filtered = useMemo(() => {
		// A query equal to the pick is not a filter — it is the chosen name
		// sitting in a `single` box, and filtering by it would hide every sibling.
		const needle =
			single !== null && query === single ? "" : query.trim().toLowerCase();
		return groups
			.map((group) => ({
				...group,
				candidates: group.candidates.filter((candidate) =>
					candidate.name.toLowerCase().includes(needle),
				),
			}))
			.filter((group) => group.candidates.length > 0);
	}, [groups, query, single]);

	const flat = useMemo<FlatOption[]>(
		() =>
			filtered.flatMap((group) =>
				group.candidates.map((candidate) => ({ name: candidate.name })),
			),
		[filtered],
	);

	// A shrinking list must not leave the highlight past its end.
	useEffect(() => {
		setActiveIndex((current) => (current < flat.length ? current : 0));
	}, [flat.length]);

	const optionId = (index: number) => `${generatedId}-option-${index}`;

	const toggle = useCallback(
		(name: string) => {
			if (!multiple) {
				// One answer: the question is over. The name goes in the box and
				// the list closes behind it.
				onChange([name]);
				setQuery(name);
				setOpen(false);
				return;
			}
			// Kept in the order they were picked, so the chips read as a list of
			// decisions and removing one does not reshuffle the rest.
			onChange(
				values.includes(name)
					? values.filter((picked) => picked !== name)
					: [...values, name],
			);
			// The search that found this name is spent; the list stays open for
			// the next one.
			setQuery("");
			setActiveIndex(0);
		},
		[multiple, onChange, values],
	);

	// Clicking away closes the list. The picks survive it — they are chips in
	// the box, not something half-typed.
	useEffect(() => {
		if (!open) return undefined;
		const onPointerDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", onPointerDown);
		return () => document.removeEventListener("mousedown", onPointerDown);
	}, [open]);

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Escape") {
			setOpen(false);
			return;
		}
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			if (!open) {
				setOpen(true);
				return;
			}
			if (flat.length === 0) return;
			const step = event.key === "ArrowDown" ? 1 : -1;
			setActiveIndex((current) => (current + step + flat.length) % flat.length);
			return;
		}
		if (event.key === "Enter") {
			// Swallowed even when nothing is highlighted: this field sits in a
			// form, and Enter on free text must never submit a typed name.
			event.preventDefault();
			if (open && flat[activeIndex]) toggle(flat[activeIndex].name);
		}
	};

	let flatIndex = -1;

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<div className={`${BOX} ${disabled ? "bg-gray-50" : ""}`}>
					{/* Chips are a `multiple` thing: with one answer the box itself
					    shows it, the way every select on this page always has. */}
					{multiple &&
						values.map((name) => (
							<span
								key={name}
								className="flex items-center gap-1 rounded bg-[#e6f2f1] px-1.5 py-0.5 text-sm text-[#004C4B]"
							>
								{name}
								<button
									type="button"
									onClick={() => toggle(name)}
									disabled={disabled}
									aria-label={`Remove ${name}`}
									className="text-[#004C4B] hover:text-[#008782] disabled:opacity-60"
								>
									<X className="h-3 w-3" aria-hidden="true" />
								</button>
							</span>
						))}
					<input
						ref={inputRef}
						id={inputId}
						type="text"
						role="combobox"
						aria-expanded={open}
						aria-controls={listboxId}
						aria-autocomplete="list"
						aria-activedescendant={
							open && flat[activeIndex] ? optionId(activeIndex) : undefined
						}
						autoComplete="off"
						disabled={disabled}
						// Once a chip is in the box, the chip says what was picked; a
						// placeholder beside it still reading "All borrowers in this
						// project" would contradict it. In `single` the box holds the
						// chosen name itself, so the placeholder is already hidden.
						placeholder={multiple && values.length > 0 ? "" : placeholder}
						value={query}
						onChange={(event) => {
							// Typing is filtering, and only filtering: it never becomes a
							// value. In `single` it does unpick, because there the box IS
							// the display of the pick — including when it is emptied.
							setQuery(event.target.value);
							if (!multiple && single !== null) onChange([]);
							setOpen(true);
							setActiveIndex(0);
						}}
						onFocus={() => setOpen(true)}
						onKeyDown={onKeyDown}
						className="min-w-[6rem] flex-1 border-0 bg-transparent p-0 text-base text-text-base shadow-none focus:outline-none focus:ring-0"
					/>
				</div>
				{/* A real button, not a decoration: the operator clicked the arrow
				    and nothing happened. It toggles the list and hands focus to the
				    input so the keyboard keeps working; mousedown rather than click
				    so the input's blur does not close what we are opening. */}
				<button
					type="button"
					tabIndex={-1}
					disabled={disabled}
					aria-label={open ? "Hide options" : "Show options"}
					onMouseDown={(event) => {
						event.preventDefault();
						if (disabled) return;
						setOpen((current) => !current);
						inputRef.current?.focus();
					}}
					className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-text-muted hover:text-[#004C4B] disabled:opacity-60"
				>
					<ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
				</button>
			</div>

			{open && (
				<div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
					<ul
						id={listboxId}
						role="listbox"
						// Stated only when it is true: a single-answer listbox that
						// advertised itself as multi-selectable would promise a screen
						// reader something the control does not do.
						aria-multiselectable={multiple ? true : undefined}
						aria-label={listLabel}
					>
						{flat.length === 0 ? (
							<li className="px-2 py-2 text-sm text-text-muted">{emptyLabel}</li>
						) : (
							filtered.map((group) => (
								<li key={`${group.folder_id ?? "unfiled"}-${group.folder_name}`}>
									{/* Read off `groups`, not `filtered`: a heading that appeared
									    and vanished as the query narrowed the list to one folder
									    would jump the popup under the cursor. */}
									{groups.length > 1 && (
										<div className="border-b border-gray-100 bg-gray-50 px-2 py-1 text-xs font-medium uppercase tracking-wide text-text-muted">
											{group.folder_name}
										</div>
									)}
									<ul role="group" aria-label={group.folder_name}>
										{group.candidates.map((candidate) => {
											flatIndex += 1;
											const index = flatIndex;
											const active = index === activeIndex;
											const picked = chosen.has(candidate.name);
											return (
												<li
													key={candidate.name}
													id={optionId(index)}
													role="option"
													aria-selected={picked}
													onMouseEnter={() => setActiveIndex(index)}
													// The input keeps focus, so this is a mousedown
													// handler rather than a click: a blur between
													// press and release would close the list first.
													onMouseDown={(event) => {
														event.preventDefault();
														toggle(candidate.name);
													}}
													className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-base ${
														active ? "bg-gray-100" : ""
													}`}
												>
													<span className="flex items-center gap-1.5 text-text-base">
														{/* The same slot in both modes, so two of these
														    fields side by side line up: a square that
														    ticks when the list takes several answers, a
														    circle that fills when it takes one. Not a
														    real <input>, because the option is itself
														    the control the mouse and keyboard drive. */}
														<span
															aria-hidden="true"
															className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center border ${
																multiple ? "rounded-sm" : "rounded-full"
															} ${
																picked
																	? "border-[#008782] bg-[#008782] text-white"
																	: "border-gray-300"
															}`}
														>
															{picked &&
																(multiple ? (
																	<Check className="h-3 w-3" />
																) : (
																	<span className="h-1.5 w-1.5 rounded-full bg-white" />
																))}
														</span>
														{candidate.name}
													</span>
													<span className="flex-shrink-0 text-sm text-text-muted">
														{countLabel(candidate.file_count)}
													</span>
												</li>
											);
										})}
									</ul>
								</li>
							))
						)}
					</ul>
				</div>
			)}
		</div>
	);
};

export default BorrowerPicker;
